/**
 * Smart Dropdown Menu System
 * Sistema universal de dropdown com posicionamento inteligente
 * 
 * Como usar:
 * 1. Adicione data-dropdown-toggle="ID_DO_DROPDOWN" no botão
 * 2. Adicione id="ID_DO_DROPDOWN" e classe "dropdown-menu" no dropdown
 * 
 * Exemplo:
 * <button data-dropdown-toggle="meu-dropdown">Toggle</button>
 * <div id="meu-dropdown" class="dropdown-menu">Conteúdo</div>
 */

(function() {
    'use strict';

    class SmartDropdown {
        constructor() {
            this.activeDropdown = null;
            this.isInitialized = false;
            this.init();
        }

        init() {
            if (this.isInitialized) return;
            
            // Espera o DOM estar pronto
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setupEvents());
            } else {
                this.setupEvents();
            }
            
            this.isInitialized = true;
        }

        setupEvents() {
            
            // Event listeners
            document.addEventListener('click', this.handleClick.bind(this));
            document.addEventListener('keydown', this.handleKeydown.bind(this));
            window.addEventListener('resize', this.handleResize.bind(this));
            window.addEventListener('scroll', this.handleScroll.bind(this));
        }

        /**
         * Manipula todos os cliques do documento
         */
        handleClick(event) {
            const toggleElement = event.target.closest('[data-dropdown-toggle]');
            
            if (toggleElement) {
                event.preventDefault();
                event.stopPropagation();
                this.handleToggleClick(toggleElement);
                return;
            }

            // Clique fora - fecha dropdown ativo
            if (this.activeDropdown && !this.activeDropdown.contains(event.target)) {
                this.closeDropdown();
            }
        }

        /**
         * Manipula clique no toggle
         */
        handleToggleClick(toggle) {
            const dropdownId = toggle.getAttribute('data-dropdown-toggle');
            const dropdown = document.getElementById(dropdownId);

            if (!dropdown) {
                console.warn(`Dropdown com ID "${dropdownId}" não encontrado`);
                return;
            }

            // Se já está ativo, fecha
            if (this.activeDropdown === dropdown) {
                this.closeDropdown();
                return;
            }

            // Fecha dropdown ativo se houver
            if (this.activeDropdown) {
                this.closeDropdown();
            }

            // Abre o novo dropdown
            this.openDropdown(toggle, dropdown);
        }

        /**
         * Abre dropdown
         */
        openDropdown(toggle, dropdown) {
            // Marca como ativo
            toggle.classList.add('dropdown-active');
            dropdown.classList.add('show');
            this.activeDropdown = dropdown;

            // Posiciona
            this.positionDropdown(toggle, dropdown);

            // Evento customizado
            this.dispatchEvent('dropdown:opened', { toggle, dropdown });
        }

        /**
         * Fecha dropdown
         */
        closeDropdown() {
            if (!this.activeDropdown) return;

            const toggle = document.querySelector(`[data-dropdown-toggle="${this.activeDropdown.id}"]`);
            
            // Remove classes
            if (toggle) {
                toggle.classList.remove('dropdown-active');
            }
            this.activeDropdown.classList.remove('show');

            // Evento customizado
            this.dispatchEvent('dropdown:closed', { toggle, dropdown: this.activeDropdown });

            this.activeDropdown = null;
        }

        /**
         * Calcula e aplica posicionamento
         */
        positionDropdown(toggle, dropdown) {
            // Reset de estilos para medição
            dropdown.style.position = 'absolute';
            dropdown.style.top = '';
            dropdown.style.left = '';
            dropdown.style.right = '';
            dropdown.style.bottom = '';
            dropdown.style.transform = '';

            // Obter dimensões
            const toggleRect = toggle.getBoundingClientRect();
            const dropdownRect = dropdown.getBoundingClientRect();
            const viewport = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            const scroll = {
                x: window.pageXOffset || document.documentElement.scrollLeft,
                y: window.pageYOffset || document.documentElement.scrollTop
            };

            // Posição inicial - centro do toggle
            let left = toggleRect.left + scroll.x + (toggleRect.width / 2) - (dropdownRect.width / 2);
            let top = toggleRect.bottom + scroll.y + 8; // 8px de margem

            // Ajustar limites horizontais
            const margin = 16;
            const minLeft = scroll.x + margin;
            const maxLeft = scroll.x + viewport.width - dropdownRect.width - margin;

            left = Math.max(minLeft, Math.min(left, maxLeft));

            // Verificar se cabe verticalmente
            const spaceBelow = viewport.height - (toggleRect.bottom - scroll.y);
            const spaceAbove = toggleRect.top - scroll.y;
            
            let positionAbove = false;

            // Se não cabe embaixo mas cabe em cima
            if (spaceBelow < dropdownRect.height + margin && spaceAbove > dropdownRect.height + margin) {
                top = toggleRect.top + scroll.y - dropdownRect.height - 8;
                positionAbove = true;
                dropdown.classList.add('dropdown-above');
            } else {
                dropdown.classList.remove('dropdown-above');
            }

            // Garantir que não saia da tela verticalmente
            const maxTop = scroll.y + viewport.height - dropdownRect.height - margin;
            const minTop = scroll.y + margin;
            
            top = Math.max(minTop, Math.min(top, maxTop));

            // Aplicar posição
            dropdown.style.left = left + 'px';
            dropdown.style.top = top + 'px';

            // Adicionar seta
            this.addArrow(toggle, dropdown, toggleRect, left, scroll.x, positionAbove);
        }

        /**
         * Adiciona seta indicativa
         */
        addArrow(toggle, dropdown, toggleRect, dropdownLeft, scrollX, isAbove) {
            // Remove seta existente
            const existingArrow = dropdown.querySelector('.dropdown-arrow');
            if (existingArrow) {
                existingArrow.remove();
            }

            // Cria nova seta
            const arrow = document.createElement('div');
            arrow.className = 'dropdown-arrow';

            // Calcula posição da seta
            const toggleCenter = toggleRect.left + scrollX + (toggleRect.width / 2);
            const arrowLeft = toggleCenter - dropdownLeft;
            
            // Limita posição da seta dentro do dropdown
            const minArrowLeft = 12;
            const maxArrowLeft = dropdown.offsetWidth - 12;
            const finalArrowLeft = Math.max(minArrowLeft, Math.min(arrowLeft, maxArrowLeft));

            arrow.style.left = finalArrowLeft + 'px';
            
            dropdown.appendChild(arrow);
        }

        /**
         * Manipula teclas
         */
        handleKeydown(event) {
            if (event.key === 'Escape' && this.activeDropdown) {
                this.closeDropdown();
                
                // Retorna foco ao toggle
                const toggle = document.querySelector(`[data-dropdown-toggle="${this.activeDropdown.id}"]`);
                if (toggle) {
                    toggle.focus();
                }
            }
        }

        /**
         * Reposiciona no resize
         */
        handleResize() {
            if (this.activeDropdown) {
                const toggle = document.querySelector(`[data-dropdown-toggle="${this.activeDropdown.id}"]`);
                if (toggle) {
                    this.positionDropdown(toggle, this.activeDropdown);
                }
            }
        }

        /**
         * Reposiciona no scroll
         */
        handleScroll() {
            if (this.activeDropdown) {
                const toggle = document.querySelector(`[data-dropdown-toggle="${this.activeDropdown.id}"]`);
                if (toggle) {
                    this.positionDropdown(toggle, this.activeDropdown);
                }
            }
        }

        /**
         * Dispara evento customizado
         */
        dispatchEvent(eventName, detail) {
            const event = new CustomEvent(eventName, {
                detail: detail,
                bubbles: true,
                cancelable: true
            });
            document.dispatchEvent(event);
        }

        /**
         * Injeta CSS necessário
         */
        injectCSS() {
            if (document.getElementById('smart-dropdown-styles')) return;

            const css = `
                .dropdown-menu {
                    position: absolute;
                    z-index: 9999;
                    visibility: hidden;
                    opacity: 0;
                    transform: translateY(-10px);
                    transition: all 0.25s ease;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    min-width: 180px;
                    max-width: 320px;
                }

                .dropdown-menu.show {
                    visibility: visible;
                    opacity: 1;
                    transform: translateY(0);
                }

                .dropdown-menu.dropdown-above {
                    transform: translateY(10px);
                }

                .dropdown-menu.dropdown-above.show {
                    transform: translateY(0);
                }

                .dropdown-arrow {
                    position: absolute;
                    width: 12px;
                    height: 12px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-right: none;
                    border-bottom: none;
                    transform: rotate(45deg);
                    top: -6px;
                    z-index: -1;
                }

                .dropdown-menu.dropdown-above .dropdown-arrow {
                    top: auto;
                    bottom: -6px;
                    border: 1px solid #e2e8f0;
                    border-left: none;
                    border-top: none;
                    transform: rotate(45deg);
                }

                .dropdown-toggle.dropdown-active {
                    /* Opcional: estilo para toggle ativo */
                }

                /* Responsivo */
                @media (max-width: 768px) {
                    .dropdown-menu {
                        position: fixed !important;
                        left: 16px !important;
                        right: 16px !important;
                        width: auto !important;
                        max-width: none !important;
                        min-width: auto !important;
                    }

                    .dropdown-arrow {
                        display: none;
                    }
                }

                /* Animação suave */
                @media (prefers-reduced-motion: reduce) {
                    .dropdown-menu {
                        transition: none;
                    }
                }
            `;

            const style = document.createElement('style');
            style.id = 'smart-dropdown-styles';
            style.textContent = css;
            document.head.appendChild(style);
        }

        // API Pública
        open(toggleId, dropdownId) {
            const toggle = document.getElementById(toggleId);
            const dropdown = document.getElementById(dropdownId);
            
            if (toggle && dropdown) {
                this.openDropdown(toggle, dropdown);
                return true;
            }
            return false;
        }

        close() {
            this.closeDropdown();
        }

        isOpen(dropdownId) {
            return this.activeDropdown && this.activeDropdown.id === dropdownId;
        }

        toggle(toggleId, dropdownId) {
            if (this.isOpen(dropdownId)) {
                this.close();
            } else {
                this.open(toggleId, dropdownId);
            }
        }
    }

    // Disponibilizar globalmente para compatibilidade
    window.SmartDropdown = SmartDropdown;

    // Exportar como ES Module
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = SmartDropdown;
    }
})();

// Export para ES6 Modules
export default window.SmartDropdown;